import { FC, memo, ReactNode } from 'react';

import { FloatingTabs } from '@dbt-labs/sourdough';

import { useQueryParamOption } from '@/hooks/queryParamOption';

const tabTypes = ['overview', 'performance', 'recommendations'] as const;
export type ProjectTabType = (typeof tabTypes)[number];

export type ProjectTabInfo = {
  type: ProjectTabType;
  count?: number;
};

const tabNameMap: Record<ProjectTabType, string> = {
  overview: 'Overview',
  performance: 'Performance',
  recommendations: 'Recommendations',
};

const isTabType = (input: string | undefined | null): input is ProjectTabType => {
  return !!input && tabTypes.includes(input as ProjectTabType);
};

const getTabType = (input: string | undefined | null): ProjectTabType | null => {
  return isTabType(input) ? input : null;
};

type ProjectTabsParams = {
  tabs: ProjectTabInfo[];
  children: (tabType: ProjectTabType) => ReactNode;
  show: boolean;
};

const ProjectOverviewTabs: FC<ProjectTabsParams> = ({ tabs, show, children }) => {
  const [tabParam, setTabParam] = useQueryParamOption({
    param: 'tab',
    options: tabTypes,
    validateOption: getTabType,
  });
  const activeTab = tabParam ? { type: tabParam } : tabs[0];

  return (
    <div
      className={`${show ? 'opacity-100' : 'opacity-0'} mt-10 duration-300 motion-reduce:duration-0`}
    >
      {tabs.length > 1 && (
        <div className="overflow-x-auto">
          <FloatingTabs testId="project-overview-tabs">
            {tabs.map((tabDetails) => (
              <FloatingTabs.Tab
                key={tabNameMap[tabDetails.type]}
                id={tabDetails.type}
                isActive={activeTab.type === tabDetails.type}
                onClick={(tab: ProjectTabType) => {
                  setTabParam(tab);
                }}
                count={tabDetails.count}
                testId={`project-overview-tabs-${tabDetails.type}`}
              >
                {tabNameMap[tabDetails.type]}
              </FloatingTabs.Tab>
            ))}
          </FloatingTabs>
        </div>
      )}
      <>{activeTab.type && children(activeTab.type)}</>
    </div>
  );
};

export default memo(ProjectOverviewTabs);
